// Отбор по ответственному рекрутёру.
//
// Здесь ровно то место, где серверный контракт беднее экрана, и притворяться нельзя. Кандидат
// несёт `owner_user_id` — опознаватель, а не имя. Справочник людей отдаёт маршрут
// `/api/v1/users`, но право на него (`core.user.view`) есть у администратора и руководства и
// НЕТ у рекрутёра и кадров — то есть ровно у тех, кто этим отбором и пользуется каждый день.
//
// Поэтому отбор устроен так: имена показываются тому, кому сервер их отдаёт; всем остальным
// остаётся честный и всегда работающий выбор «мои кандидаты» — тот самый, ради которого
// рекрутёр в этот отбор и лезет. Выдумывать имя по опознавателю на клиенте нельзя: в списке
// появилось бы «пользователь 9f3c…», что не отвечает ни на один вопрос.
//
// Что нужно от сервера, чтобы отбор стал полным: имя ответственного в самом ответе воронки
// либо справочник рекрутёров с именами по праву `hiring.candidate.view`. Записано в отчёте.

import { useQuery } from '@tanstack/react-query';

import { listUsers } from '@/shared/api/endpoints/users';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';

/** Право на справочник людей: у рекрутёра и кадров его нет. */
const USERS_PERMISSION = 'core.user.view';

export function OwnerFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const permissions = useAuthStore((state) => state.permissions);
  const me = useAuthStore((state) => state.user);
  const мнеВиденСправочник = can(permissions, USERS_PERMISSION);

  const справочник = useQuery({
    queryKey: ['users', 'list', 'active'],
    queryFn: ({ signal }) => listUsers({ active_only: true }, signal),
    enabled: мнеВиденСправочник,
    // Список пользователей меняется реже, чем открывается этот экран: держим пять минут,
    // иначе каждое открытие раздела стучится за одним и тем же перечнем.
    staleTime: 5 * 60 * 1000,
  });

  const варианты: { value: string; label: string }[] = [];
  if (me !== null) {
    варианты.push({ value: me.id, label: 'Мои кандидаты' });
  }
  for (const user of справочник.data?.items ?? []) {
    if (user.id === me?.id) {
      continue; // себя уже назвали словами «мои кандидаты», второй строкой не двоим
    }
    варианты.push({ value: user.id, label: user.display_name });
  }
  // Ссылка пришла с ответственным, которого нет в нашем перечне (у нас нет справочника либо
  // человек снят с учёта). Отбор при этом РАБОТАЕТ — он уехал на сервер, — и кнопка обязана
  // остаться выбранной: иначе список отобран, а поле показывает «любой ответственный».
  if (value !== undefined && !варианты.some((вариант) => вариант.value === value)) {
    варианты.push({ value, label: 'Выбранный ответственный' });
  }

  return (
    <SelectField
      id='hiring-owner'
      label='Ответственный'
      value={value ?? NO_VALUE}
      onChange={(next) => onChange(next === NO_VALUE ? undefined : next)}
      options={варианты}
      noneLabel='Любой ответственный'
      placeholder='Любой ответственный'
      pending={мнеВиденСправочник && справочник.isPending}
      failed={мнеВиденСправочник && справочник.isError}
      hint={
        мнеВиденСправочник || варианты.length === 0
          ? undefined
          : 'Имена остальных рекрутёров показывает справочник людей, а он открыт администратору и руководству.'
      }
    />
  );
}
